import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configApi, type StatusTaskTemplates, type TaskTemplate } from '../../api/config';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../types/api';
import Card from '../ui/Card';

const ENTITIES: { key: 'verfahren' | 'anfrage' | 'inserat'; label: string }[] = [
  { key: 'verfahren', label: 'Verfahren' },
  { key: 'anfrage', label: 'Anfragen' },
  { key: 'inserat', label: 'Inserate' },
];

export default function StatusTaskTemplatesEditor() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = user?.role === 'city_admin' || user?.role === 'system_admin';

  const { data: statusConfig } = useQuery({
    queryKey: ['config', 'status'],
    queryFn: configApi.getStatusConfig,
  });
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['config', 'status-task-templates'],
    queryFn: configApi.getStatusTaskTemplates,
  });

  const [draft, setDraft] = useState<StatusTaskTemplates>({});
  const [errorMsg, setErrorMsg] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (templates) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize the editable draft when async templates arrive.
      setDraft(templates);
    }
  }, [templates]);

  const rowsFor = (entity: string, status: string): TaskTemplate[] => draft[entity]?.[status] ?? [];

  const setRows = (entity: string, status: string, rows: TaskTemplate[]) => {
    setDraft((d) => ({ ...d, [entity]: { ...(d[entity] ?? {}), [status]: rows } }));
  };

  const mutation = useMutation({
    mutationFn: () => {
      // leere Titel herausfiltern
      const cleaned: StatusTaskTemplates = {};
      for (const [etype, statusMap] of Object.entries(draft)) {
        cleaned[etype] = {};
        for (const [status, rows] of Object.entries(statusMap)) {
          const valid = rows.filter((r) => r.title.trim());
          if (valid.length) cleaned[etype][status] = valid.map((r) => ({
            title: r.title.trim(),
            ...(r.frist_tage != null && !Number.isNaN(r.frist_tage) ? { frist_tage: r.frist_tage } : {}),
          }));
        }
      }
      return configApi.updateStatusTaskTemplates(cleaned);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['config', 'status-task-templates'], result);
      queryClient.invalidateQueries({ queryKey: ['aufgaben', 'suggestions'] });
      setSavedAt(Date.now());
      setErrorMsg('');
    },
    onError: (err) => setErrorMsg(getApiErrorMessage(err, 'Fehler beim Speichern')),
  });

  if (isLoading) return <p className="text-sm text-gray-500">Lädt...</p>;
  if (error) return <p className="text-sm text-red-600">Fehler beim Laden.</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Legen Sie je Status fest, welche Aufgaben als nächster Schritt vorgeschlagen werden (mit optionaler
        Frist in Tagen). Vorschläge erscheinen auf der Detailseite und werden per Klick angelegt — nichts
        wird automatisch erzeugt.
      </p>

      {ENTITIES.map((ent) => {
        const states = statusConfig?.[ent.key] ?? [];
        return (
          <div key={ent.key}>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{ent.label}</h4>
            <div className="space-y-3">
              {states.map((st) => {
                const rows = rowsFor(ent.key, st.key);
                return (
                  <Card key={st.key} className="bg-transparent border border-gray-200 shadow-none p-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Status: {st.label}</p>
                    <div className="space-y-2">
                      {rows.map((row, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            value={row.title}
                            onChange={(e) => {
                              const next = [...rows];
                              next[i] = { ...row, title: e.target.value };
                              setRows(ent.key, st.key, next);
                            }}
                            placeholder="Aufgaben-Titel"
                            disabled={!canEdit}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <input
                            type="number"
                            value={row.frist_tage ?? ''}
                            onChange={(e) => {
                              const next = [...rows];
                              const v = e.target.value === '' ? null : Number(e.target.value);
                              next[i] = { ...row, frist_tage: v };
                              setRows(ent.key, st.key, next);
                            }}
                            placeholder="Frist (Tage)"
                            disabled={!canEdit}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          {canEdit && (
                            <button
                              onClick={() => setRows(ent.key, st.key, rows.filter((_, j) => j !== i))}
                              className="text-gray-400 hover:text-red-600 text-sm"
                              title="Entfernen"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      {canEdit && (
                        <button
                          onClick={() => setRows(ent.key, st.key, [...rows, { title: '', frist_tage: null }])}
                          className="btn btn-text text-xs"
                        >
                          + Aufgabe
                        </button>
                      )}
                      {rows.length === 0 && !canEdit && (
                        <p className="text-xs text-gray-400">Keine Vorlage.</p>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
      {canEdit && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="btn btn-primary"
          >
            {mutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
          </button>
          {savedAt && <span className="text-xs text-green-700">Gespeichert.</span>}
        </div>
      )}
    </div>
  );
}
